import { budgetMinutes } from './project'
import type { Project } from './project'
import type { Task } from './task'
import type { TimeEntry } from './time-entry'

// Bare numbers are hours here, unlike the timesheet duration input.
export function parseBudgetHours(value: string): number | null | undefined {
  const input = value.trim()
  if (!input) return null
  let minutes: number
  const clock = /^(\d+):(\d{2})$/.exec(input)
  if (clock) {
    if (Number(clock[2]) >= 60) return undefined
    minutes = Number(clock[1]) * 60 + Number(clock[2])
  } else {
    if (!/^\d+(?:[.,]\d+)?$/.test(input)) return undefined
    const raw = Number(input.replace(',', '.')) * 60
    minutes = Math.round(raw)
    if (Math.abs(raw - minutes) > 0.000001) return undefined
  }
  return budgetMinutes.safeParse(minutes).success ? minutes : undefined
}

export function buildBudgetOverview(projects: Project[], tasks: Task[], entries: TimeEntry[]) {
  const projectTotals = new Map<string, number>()
  const taskTotals = new Map<string, number>()
  const unassigned = new Map<string, number>()
  for (const entry of entries) {
    projectTotals.set(
      entry.projectId,
      (projectTotals.get(entry.projectId) ?? 0) + entry.durationMinutes
    )
    const totals = entry.taskId ? taskTotals : unassigned
    const id = entry.taskId ?? entry.projectId
    totals.set(id, (totals.get(id) ?? 0) + entry.durationMinutes)
  }
  const tasksByProject = new Map<string, Task[]>()
  for (const task of tasks) {
    const group = tasksByProject.get(task.projectId) ?? []
    group.push(task)
    tasksByProject.set(task.projectId, group)
  }
  const rows = projects.map((project) => ({
    ...project,
    trackedMinutes: projectTotals.get(project.id) ?? 0,
    unassignedMinutes: unassigned.get(project.id) ?? 0,
    tasks: (tasksByProject.get(project.id) ?? []).map((task) => ({
      ...task,
      trackedMinutes: taskTotals.get(task.id) ?? 0
    }))
  }))
  const totals = rows.reduce(
    (sum, row) => {
      sum.trackedMinutes += row.trackedMinutes
      if (row.budgetMinutes === null) sum.unbudgetedMinutes += row.trackedMinutes
      else {
        sum.budgetMinutes += row.budgetMinutes
        sum.budgetedProjects += 1
        sum.remainingMinutes += Math.max(0, row.budgetMinutes - row.trackedMinutes)
        sum.overMinutes += Math.max(0, row.trackedMinutes - row.budgetMinutes)
        if (row.trackedMinutes > row.budgetMinutes) sum.overProjects += 1
      }
      return sum
    },
    {
      trackedMinutes: 0,
      budgetMinutes: 0,
      budgetedProjects: 0,
      remainingMinutes: 0,
      overMinutes: 0,
      overProjects: 0,
      unbudgetedMinutes: 0
    }
  )
  return { rows, totals }
}
